import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { ARTICLES } from "@/data/articles";

export default function ArticlePage() {
  const { slug } = useParams();
  const idx = ARTICLES.findIndex((a) => a.slug === slug);
  if (idx === -1) return <Navigate to="/resources" replace />;
  const article = ARTICLES[idx];
  const next = ARTICLES[(idx + 1) % ARTICLES.length];

  return (
    <div data-testid="article-page" className="pt-20">
      <article className="mx-auto max-w-3xl px-6 py-20 lg:py-28">
        <Reveal>
          <Link
            to="/resources#articles"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-steel-muted transition-colors hover:text-steel-text"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Resource Center
          </Link>
          <div className="mt-10 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">
            <span className="text-steel-brass">{article.category}</span>
            <span>·</span>
            <span>{article.minutes} min read</span>
          </div>
          <h1 className="mt-6 font-display text-4xl font-medium leading-[1.08] tracking-tight text-steel-text sm:text-5xl">
            {article.title}
          </h1>
        </Reveal>

        <div className="mt-12 space-y-10">
          {article.body.map((section, i) => (
            <Reveal key={i} delay={0.05}>
              <div>
                {section.h && (
                  <h2 className="font-display text-2xl font-medium text-steel-text">{section.h}</h2>
                )}
                {section.ps.map((p, j) => (
                  <p key={j} className={`${section.h ? "mt-4" : ""} ${j > 0 ? "mt-4" : ""} text-base leading-relaxed text-steel-secondary sm:text-lg`}>
                    {p}
                  </p>
                ))}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16 border border-steel-border bg-steel-surface p-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-brass">Your situation</p>
            <p className="mt-3 text-base leading-relaxed text-steel-secondary">
              General information only — your return may differ. If this article sounds like your situation,
              let's look at it properly.
            </p>
            <Link
              to="/get-started"
              data-testid="article-cta-btn"
              className="group mt-6 inline-flex items-center gap-2 bg-steel-accent px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-steel-accentHover"
            >
              Start My Tax Return
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <Link
            to={`/resources/${next.slug}`}
            data-testid="article-next-link"
            className="group mt-16 flex items-center justify-between border-t border-steel-border pt-8"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">Next article</p>
              <p className="mt-2 font-display text-xl font-medium text-steel-text group-hover:text-steel-brass">{next.title}</p>
            </div>
            <ArrowUpRight className="h-5 w-5 text-steel-muted transition-colors group-hover:text-steel-brass" />
          </Link>
        </Reveal>
      </article>
    </div>
  );
}
